import React from "react";
import { NumericFormat } from "react-number-format";
import { Box, CircularProgress } from "@mui/material";
import { calculatePremium } from "../../premium/domain/calculatePremium";

export const scrollToTop = () => {
  window.scrollTo({ top: 0, behavior: "smooth" });
};

export function formatAsCurrency(value) {
  if (!value) return "";
  const numericValue = Number.parseFloat(value);
  return numericValue.toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
  });
}

export const parseCurrencyToNumber = (value) => {
  if (value === null || value === undefined) return Number.NaN;
  if (typeof value === "number") return Number.isFinite(value) ? value : Number.NaN;

  const raw = String(value).trim();
  if (!raw) return Number.NaN;

  const noThousands = raw.replaceAll(",", "");
  const cleaned = noThousands.replace(/[^0-9.]/g, "");
  if (!cleaned) return Number.NaN;

  const [intPart, ...fracParts] = cleaned.split(".");
  const normalized = fracParts.length ? `${intPart}.${fracParts.join("")}` : intPart;

  const num = Number(normalized);
  return Number.isFinite(num) ? num : Number.NaN;
};

export const safeNumber = (num, fallback = 0) =>
  Number.isFinite(num) ? num : fallback;

export function recalcGuardianshipPremiumFromBasicInfo({
  bondAmount,
  dateOfDeath,
  bondSubCategory,
}) {
  const amount = parseCurrencyToNumber(bondAmount);
  if (!Number.isFinite(amount) || !dateOfDeath) return null;
  return calculatePremium(amount, dateOfDeath, bondSubCategory);
}

export const splitEmploymentOccupation = (raw) => {
  const value = (raw || "").trim();
  if (!value) return { employmentStatus: "", occupationText: "" };
  const parts = value.split("-").map((p) => p.trim()).filter(Boolean);
  if (parts.length === 0) return { employmentStatus: "", occupationText: "" };
  const recognized = new Set(["Employed", "Unemployed", "Retired"]);
  const first = parts[0];
  if (!recognized.has(first)) {
    return { employmentStatus: "Employed", occupationText: value };
  }
  return { employmentStatus: first, occupationText: parts.slice(1).join(" - ") };
};

export function getBlanketEmailHelperText(emailChecking, emailExists, hasError) {
  if (emailChecking) return "Checking email...";
  if (emailExists) return "This email already exists.";
  if (hasError) return "Email is required";
  return "";
}

export function getSelectedDocumentFileName(selectedFile, fallbackFileName) {
  if (selectedFile) return selectedFile.name;
  return fallbackFileName || "No file selected.";
}

export function renderDocumentFileStatus(isUploading, selectedFile, fallbackFileName) {
  if (isUploading) {
    return (
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <CircularProgress size={16} />
        <span>Uploading...</span>
      </Box>
    );
  }
  return getSelectedDocumentFileName(selectedFile, fallbackFileName);
}

export const joinEmploymentOccupation = (employmentStatus, occupationText) => {
  const status = (employmentStatus || "").trim();
  const occ = (occupationText || "").trim();
  if (!status) return "";
  if (status === "Unemployed") return "Unemployed";
  if (!occ) return status;
  return `${status} - ${occ}`;
};

export function getOccupationQuestionLabel(employmentStatus) {
  if (employmentStatus === "Retired") return "What occupation did you retire from?";
  if (employmentStatus === "Employed") return "What is your current occupation?";
  return "Occupation";
}

export const NumberFormatCustom = React.forwardRef(function NumberFormatCustom(
  props,
  ref
) {
  const { onChange, ...other } = props;

  return (
    <NumericFormat
      {...other}
      getInputRef={ref}
      onValueChange={(values) => {
        onChange({
          target: {
            name: props.name,
            value: values.value,
          },
        });
      }}
      thousandSeparator
      isNumericString
      prefix="$"
      fixedDecimalScale
    />
  );
});
